import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { InputLabel } from "./forms/FormElements";
import SearchSelect from "./forms/SearchSelect";
import ConfirmModal from "./ConfirmModal";
import apiHandler from "../../js/apiHandler";
import { COUNTRIES } from "../../js/constants";
import { fetchOrgDetails } from "../../helpers/helpers";

/**
 * StripeSetupTrigger
 *
 * A self-contained, reusable component that:
 *   - Checks whether the current org has a Stripe account
 *   - Renders a clickable notification item when one is not set up
 *   - Opens the Stripe setup modal on click
 *
 * Props:
 *   onVisible(bool)  – called when visibility changes; use to update badge counts
 *   open     {bool}  – controlled mode: skip nc-item, just show the modal
 *   onClose  {fn}    – controlled mode: called when the modal is dismissed
 *
 * Drop anywhere in the app. Used inside NotificationCenter (autonomous) and
 * StripeSection (controlled via open/onClose).
 */
const StripeSetupTrigger = ({ org, user, onVisible, open: controlledOpen, onClose }) => {
	const isControlled = controlledOpen !== undefined;
	const [stripeStatus, setStripeStatus] = useState(null); // null=loading, 'notConnected', 'pendingSetup', 'connected'
	const [modalOpen, setModalOpen] = useState(false);
	const [pendingLoading, setPendingLoading] = useState(false);
	const [country, setCountry] = useState(
		COUNTRIES.some((c) => c.value === user?.contact_info?.country) ? user.contact_info.country : "US",
	);

	useEffect(() => {
		if (isControlled || !org) return;
		fetchOrgDetails(org)
			.then((details) => {
				const pm = details?.property_manager_details;
				if (pm?.payment_account_id != null) {
					setStripeStatus("connected");
				} else if (pm?.preliminary_payment_account_id != null) {
					setStripeStatus("pendingSetup");
				} else {
					setStripeStatus("notConnected");
				}
			})
			.catch(() => setStripeStatus(null));
	}, [org]);

	// Notify parent when visibility changes
	useEffect(() => {
		if (stripeStatus !== null) {
			onVisible?.(stripeStatus === "notConnected" || stripeStatus === "pendingSetup");
		}
	}, [stripeStatus, onVisible]);

	const handleConnect = async () => {
		const res = await apiHandler.post({
			apiName: "accapi",
			path: `/organizations/${org}/payment_account`,
			options: {
				responseType: "text",
				body: {
					return_url: `${window.location.origin}/payments/stripe`,
					country,
				},
			},
		});
		if (res?.data) {
			window.location.href = res.data;
		}
	};

	const handleCompleteSetup = async () => {
		if (pendingLoading) return;
		setPendingLoading(true);
		try {
			await handleConnect();
		} finally {
			setPendingLoading(false);
		}
	};

	if (!isControlled && stripeStatus !== "notConnected" && stripeStatus !== "pendingSetup") return null;

	const showModal = isControlled ? controlledOpen : modalOpen;
	const hideModal = isControlled ? () => onClose?.() : () => setModalOpen(false);

	return (
		<>
			{!isControlled && stripeStatus === "notConnected" && (
				<div
					className="nc-item nc-item--warning nc-item--unread"
					role="button"
					tabIndex={0}
					onClick={() => setModalOpen(true)}
					onKeyDown={(e) => e.key === "Enter" && setModalOpen(true)}
					style={{ cursor: "pointer" }}>
					<span className="nc-item__dot" />
					<div className="nc-item__body">
						<div className="nc-item__title">Set up Stripe payouts</div>
						<div className="nc-item__message">
							Connect your Stripe account to receive payments.
						</div>
					</div>
				</div>
			)}
			{!isControlled && stripeStatus === "pendingSetup" && (
				<div
					className="nc-item nc-item--warning nc-item--unread"
					role="button"
					tabIndex={0}
					onClick={handleCompleteSetup}
					onKeyDown={(e) => e.key === "Enter" && handleCompleteSetup()}
					style={{ cursor: pendingLoading ? "wait" : "pointer" }}>
					<span className="nc-item__dot" />
					<div className="nc-item__body">
						<div className="nc-item__title">Complete your Stripe setup</div>
						<div className="nc-item__message">
							Finish setting up your Stripe account to start accepting payments.
						</div>
					</div>
				</div>
			)}

			<ConfirmModal
				show={showModal}
				onHide={hideModal}
				title="Confirm your country of residence"
				confirmLabel="Confirm"
				onConfirm={handleConnect}
				errorMsg="Failed to connect Stripe. Please try again or contact support."
				body={
					<div className="bottom-margin">
						<InputLabel>
							<strong>Country</strong>
						</InputLabel>
						<SearchSelect
							options={COUNTRIES}
							value={country}
							searchPlaceholder="Search countries"
							onChange={setCountry}
							maxHeight={300}
						/>
					</div>
				}
			/>
		</>
	);
};

export default connect((state) => ({
	org: state.roleManager.org,
	user: state.authState.user,
}))(StripeSetupTrigger);
